// 지원되는 언어 타입
export type SupportedLanguage = 'en' | 'ko' | 'ja' | 'zh-CN' | 'zh-TW';

// 기본 언어 설정
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// 언어 변경 이벤트 이름 상수화
export const LANGUAGE_CHANGE_EVENT = 'language-changed';

/**
 * 브라우저 언어를 감지하여 지원되는 언어로 변환
 * @returns 지원되는 언어 코드
 */
export const detectBrowserLanguage = (): SupportedLanguage => {
  const browserLang = navigator.language.toLowerCase();
  
  // 지원하는 언어 중에서 매칭되는 것 찾기
  if (browserLang.startsWith('ko')) return 'ko';
  if (browserLang.startsWith('ja')) return 'ja';
  if (browserLang.startsWith('zh-cn')) return 'zh-CN';
  if (browserLang.startsWith('zh-tw')) return 'zh-TW';
  if (browserLang.startsWith('zh')) {
    // 간체와 번체 구분이 없는 경우 간체로 기본 설정
    return 'zh-CN';
  }
  
  // 기본값은 영어
  return 'en';
};

/**
 * 지원 가능한 OS 언어인지 확인
 * @param lang 언어 코드
 * @returns 지원 가능한 언어이면 true (영어, 일본어, 중국어 간체, 중국어 번체)
 */
export const isSupportedOSLanguage = (lang: SupportedLanguage): boolean => {
  return ['ja', 'zh-CN', 'zh-TW'].includes(lang);
};

// 현재 언어 설정 (초기값: 디바이스 언어 또는 기본 영어)
// 브라우저 환경에서만 실행 (SSR 환경 대비)
let currentLanguage: SupportedLanguage = DEFAULT_LANGUAGE;

// 브라우저 환경에서만 실행
if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
  try {
    // 현재 브라우저 언어 감지
    const detectedLanguage = detectBrowserLanguage();
    
    // 로컬 스토리지에서 언어 설정 불러오기
    const savedLanguage = localStorage.getItem('app_language') as SupportedLanguage | null;
    const lastDetectedLanguage = localStorage.getItem('last_detected_language') as SupportedLanguage | null;
    
    // 언어 갱신이 필요한 경우들:
    // 1. 저장된 언어가 없거나 유효하지 않은 경우
    // 2. 마지막 감지된 언어가 없는 경우 (최초 실행)
    // 3. 브라우저 언어가 변경된 경우
    // 4. 저장된 언어와 현재 감지된 언어 설정이 맞지 않는 경우
    const needsLanguageUpdate = 
      !savedLanguage || 
      !['en', 'ko', 'ja', 'zh-CN', 'zh-TW'].includes(savedLanguage) ||
      !lastDetectedLanguage ||
      (lastDetectedLanguage !== detectedLanguage) ||
      // 감지된 언어가 지원되는 OS 언어이고 저장된 언어가 영어일 때 업데이트 필요
      (isSupportedOSLanguage(detectedLanguage) && savedLanguage === 'en') ||
      // 감지된 언어가 한국어 등 비지원 언어이고 저장된 언어가 해당 언어일 때 영어로 업데이트 필요
      (!isSupportedOSLanguage(detectedLanguage) && savedLanguage === detectedLanguage);
    
    if (needsLanguageUpdate) {
      // 새로운 언어 설정 결정
      
      // 요구사항에 따른 처리:
      // 1. 영어, 일본어, 번체, 간체는 해당 언어 우선 노출
      // 2. 한국어 등 이외 언어는 영어 기본 노출
      if (isSupportedOSLanguage(detectedLanguage)) {
        // 일본어, 중국어(간체/번체)는 감지된 언어 사용
        currentLanguage = detectedLanguage;
      } else {
        // 한국어나 지원되지 않는 다른 언어는 영어로 설정
        currentLanguage = 'en';
      }
      
      // 새로운 언어 설정 저장
      localStorage.setItem('app_language', currentLanguage);
      localStorage.setItem('last_detected_language', detectedLanguage);
    } else {
      // 언어 갱신이 필요하지 않은 경우 기존 설정 사용
      currentLanguage = savedLanguage;
    }
    
  } catch (error) {
    // localStorage 접근 오류 등이 발생할 경우 기본 언어 사용
    console.error('언어 설정 불러오기 실패:', error);
    currentLanguage = DEFAULT_LANGUAGE;
  }
}

/**
 * 현재 설정된 언어 반환
 * @returns 현재 언어 코드
 */
export const getCurrentLanguage = (): SupportedLanguage => {
  return currentLanguage;
};

/**
 * 디바이스 언어와 일치하는지 확인
 * @returns 현재 언어가 디바이스 언어와 일치하면 true
 */
export const isDeviceLanguage = (): boolean => {
  return currentLanguage === detectBrowserLanguage();
};

/**
 * 언어 설정 변경
 * @param language 변경할 언어 코드
 */
export const setLanguage = (language: SupportedLanguage): void => {
  // 동일한 언어로 변경 시 중복 이벤트 방지
  if (language === currentLanguage) {
    return;
  }

  currentLanguage = language;
  
  // 브라우저 환경에서만 실행
  if (typeof window !== 'undefined') {
    try {
      // 선택한 언어 설정 저장
      localStorage.setItem('app_language', language);
      
      // 언어 변경 이벤트 발생 - 모든 컴포넌트에 변경 알림
      const event = new CustomEvent(LANGUAGE_CHANGE_EVENT, { 
        detail: { 
          language,
          timestamp: Date.now()
        } 
      });
      window.dispatchEvent(event);
      
    } catch (error) {
      console.error('언어 설정 저장 실패:', error);
    }
  }
};

/**
 * 다국어 필드에서 현재 언어에 맞는 값 반환
 * 해당 언어의 값이 없으면 기본 필드 값 사용
 * 
 * @param defaultValue 기본 필드 값
 * @param translations 언어별 번역 값 객체 (예: { en: 'English value', ko: '한국어 값' })
 * @returns 현재 언어에 맞는 값 또는 기본값
 */
export const getLocalizedValue = (
  defaultValue: string,
  translations: Partial<Record<SupportedLanguage, string | null | undefined>>
): string => {
  // 인자가 유효하지 않은 경우 빈 문자열 반환
  if (!defaultValue && !translations) return '';
  
  // defaultValue가 없으면 빈 문자열 사용
  const safeDefaultValue = defaultValue || '';
  
  // translations이 없거나 객체가 아닌 경우 기본값 반환
  if (!translations || typeof translations !== 'object') {
    return safeDefaultValue;
  }
  
  // 현재 언어에 해당하는 번역 값이 있고 값이 유효하면 해당 값 반환
  const translatedValue = translations[currentLanguage];
  if (translatedValue) {
    return translatedValue;
  }
  
  // 영어 값이 있고 현재 언어가 영어가 아니면 영어 값 반환
  if (translations.en && currentLanguage !== 'en') {
    return translations.en;
  }
  
  // 그 외의 경우 기본값 반환
  return safeDefaultValue;
}; 